"use client";

import type { INewsItem } from "../../../interfaces/News.interface";
import type { INewsFilters } from "../../../interfaces/NewsFilter.interface";

interface NewsState {
  news: INewsItem[];
  page: number;
  showMore: boolean;
  showLess: boolean;
  filters: string[];
  limit: number;
  timestamp?: number; // Optional timestamp for expiration check
}

interface ScrollPosition {
  scrollY: number;
  timestamp: number;
}

const STORAGE_KEY_NEWS = "news_list_state";
const STORAGE_KEY_SCROLL = "news_scroll_position";
const STORAGE_KEY_FILTERS = "news_filter_selections";
const SCROLL_TIMEOUT = 4 * 60 * 1000; // 4 minutes
const NEWS_STATE_TIMEOUT = 4 * 60 * 1000; // 4 minutes
const FILTERS_TIMEOUT = 4 * 60 * 1000; // 4 minutes

/**
 * Store for persisting news list state across navigation
 * Uses sessionStorage to persist data within the same browser session
 */
export const newsStore = {
  /**
   * Get a unique key for storing news state based on filters
   */
  getStorageKey: (filters: string[]): string => {
    const filterKey = filters.length > 0 
      ? `_${[...filters].sort((a: string, b: string) => a.localeCompare(b)).join("_")}` 
      : "_default";
    return `${STORAGE_KEY_NEWS}${filterKey}`;
  },

  /**
   * Save news state to sessionStorage
   */
  saveNewsState: (filters: string[], state: Partial<NewsState>): void => {
    if (typeof window === "undefined") return;

    try {
      const key = newsStore.getStorageKey(filters);
      const existingState = newsStore.getNewsState(filters);
      const newState: NewsState = {
        news: state.news ?? existingState?.news ?? [],
        page: state.page ?? existingState?.page ?? 1,
        showMore: state.showMore ?? existingState?.showMore ?? false,
        showLess: state.showLess ?? existingState?.showLess ?? false,
        filters: filters,
        limit: state.limit ?? existingState?.limit ?? 5,
        timestamp: Date.now(), // Add timestamp for expiration check
      };
      sessionStorage.setItem(key, JSON.stringify(newState));
    } catch (error) {
      console.error("Failed to save news state:", error);
    }
  },

  /**
   * Get news state from sessionStorage
   */
  getNewsState: (filters: string[]): NewsState | null => {
    if (typeof window === "undefined") return null;

    try {
      const key = newsStore.getStorageKey(filters);
      const stored = sessionStorage.getItem(key);
      if (!stored) return null;

      const state = JSON.parse(stored) as NewsState;
      
      // Check expiration for all states - clear if older than 4 minutes
      if (state.timestamp) {
        const age = Date.now() - state.timestamp;
        if (age > NEWS_STATE_TIMEOUT) {
          sessionStorage.removeItem(key);
          return null;
        }
      }
      
      // Verify filters match (in case filter structure changed)
      // Create copies before sorting to avoid mutating original arrays
      const stateFiltersSorted = [...state.filters].sort((a: string, b: string) => a.localeCompare(b));
      const filtersSorted = [...filters].sort((a: string, b: string) => a.localeCompare(b));
      if (JSON.stringify(stateFiltersSorted) !== JSON.stringify(filtersSorted)) {
        return null;
      }
      return state;
    } catch (error) {
      console.error("Failed to get news state:", error);
      return null;
    }
  },

  /**
   * Clear news state from sessionStorage
   */
  clearNewsState: (filters: string[]): void => {
    if (typeof window === "undefined") return;

    try {
      const key = newsStore.getStorageKey(filters);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error("Failed to clear news state:", error);
    }
  },

  /**
   * Save scroll position before navigation
   */
  saveScrollPosition: (): void => {
    if (typeof window === "undefined") return;

    try {
      const scrollPosition: ScrollPosition = {
        scrollY: window.scrollY,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(STORAGE_KEY_SCROLL, JSON.stringify(scrollPosition));
    } catch (error) {
      console.error("Failed to save scroll position:", error);
    }
  },

  /**
   * Get and clear scroll position (should be called once after restore)
   */
  getScrollPosition: (): number | null => {
    if (typeof window === "undefined") return null;

    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_SCROLL);
      if (!stored) return null;

      const scrollPosition = JSON.parse(stored) as ScrollPosition;
      
      // Check if scroll position is too old (expired)
      const age = Date.now() - scrollPosition.timestamp;
      if (age > SCROLL_TIMEOUT) {
        sessionStorage.removeItem(STORAGE_KEY_SCROLL);
        return null;
      }

      return scrollPosition.scrollY;
    } catch (error) {
      console.error("Failed to get scroll position:", error);
      return null;
    }
  },

  /**
   * Clear scroll position after restoration
   */
  clearScrollPosition: (): void => {
    if (typeof window === "undefined") return;

    try {
      sessionStorage.removeItem(STORAGE_KEY_SCROLL);
    } catch (error) {
      console.error("Failed to clear scroll position:", error);
    }
  },

  /**
   * Save filter selections to sessionStorage
   */
  saveFilterSelections: (filters: INewsFilters): void => {
    if (typeof window === "undefined") return;

    try {
      const filterData = {
        filters,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(filterData));
    } catch (error) {
      console.error("[newsStore] Failed to save filter selections:", error);
    }
  },

  /**
   * Get filter selections from sessionStorage
   */
  getFilterSelections: (): INewsFilters | null => {
    if (typeof window === "undefined") return null;

    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_FILTERS);
      if (!stored) {
        return null;
      }

      const filterData = JSON.parse(stored) as { filters: INewsFilters; timestamp: number };
      
      // Check expiration - clear if older than 4 minutes
      const age = Date.now() - filterData.timestamp;
      if (age > FILTERS_TIMEOUT) {
        sessionStorage.removeItem(STORAGE_KEY_FILTERS);
        return null;
      }

      return filterData.filters;
    } catch (error) {
      console.error("[newsStore] Failed to get filter selections:", error);
      return null;
    }
  },

  /**
   * Clear filter selections from sessionStorage
   */
  clearFilterSelections: (): void => {
    if (typeof window === "undefined") return;

    try {
      sessionStorage.removeItem(STORAGE_KEY_FILTERS);
    } catch (error) {
      console.error("[newsStore] Failed to clear filter selections:", error);
    }
  },

  /**
   * Clear all news-related session storage items
   */
  clearAllNewsStorage: (): void => {
    if (typeof window === "undefined") return;

    try {
      // Clear all news list states (iterate through all keys)
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(STORAGE_KEY_NEWS)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      // Clear scroll position
      sessionStorage.removeItem(STORAGE_KEY_SCROLL);
      
      // Clear filter selections
      sessionStorage.removeItem(STORAGE_KEY_FILTERS);
    } catch (error) {
      console.error("[newsStore] Failed to clear all news storage:", error);
    }
  },
};

